/**
 * Phân trang giữ trên URL — nguồn sự thật duy nhất cho `page` và `perPage`.
 *
 * Trước OQ-263 mỗi màn tự lo: ba màn giữ trong `useState` (mất sạch khi F5),
 * màn Tên miền giữ trên URL nhưng dính một cái bẫy tinh vi. Bốn cách làm, bốn
 * hành vi khác nhau, và người dùng không có lý do nào để đoán được cái nào.
 *
 * ── Cái bẫy, vì nó sẽ quay lại nếu ai đó viết tay lần nữa ──────────────────
 *
 * `useSearchParams` của react-router v6 bọc hàm setter bằng
 * `useCallback(..., [navigate, searchParams])`. Nghĩa là **setter đổi identity
 * mỗi lần URL đổi**. Bất kỳ `useEffect` nào để setter ấy trong deps sẽ chạy lại
 * sau MỌI thao tác chuyển trang — kể cả thao tác chẳng liên quan gì tới nó.
 *
 * Ở màn Tên miền, effect ấy là debounce ô tìm kiếm, và thân nó gọi
 * `next.delete('page')` vô điều kiện để đưa người dùng về trang đầu khi từ khoá
 * đổi. Hệ quả: bấm "Sau" → URL thành `?page=2` → 250 ms sau tự bật về trang 1.
 * Không lỗi nào được ném ra, không có gì để lần theo — chỉ có một cái nút trông
 * như hỏng.
 *
 * Bài học chung: một effect ghi vào URL PHẢI tự kiểm "giá trị có thật sự đổi
 * không" trước khi ghi, chứ không được tin rằng deps của mình chỉ đổi khi có
 * việc cho mình làm.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Mặc định chung cho MỌI danh sách.
 *
 * `FR-038` từng đòi riêng DNS records 50 dòng. Chủ dự án bỏ ngoại lệ đó ở
 * OQ-263: *"tất cả mặc định là 25"* — một con số cho toàn hệ thống thì người
 * dùng không phải học lại ở từng màn. Tài liệu đã sửa theo.
 */
export const PER_PAGE_MAC_DINH = 25;

/** Các mức cho ô chọn số dòng — giống nhau ở mọi màn. */
export const PER_PAGE_OPTIONS = [25, 50, 100] as const;

/** Thay đổi tham số URL; `null` nghĩa là xoá khỏi URL. */
export type ThayDoiThamSo = Record<string, string | null>;

function docSo(giaTri: string | null, macDinh: number): number {
  const so = Number(giaTri);
  if (!Number.isFinite(so) || so < 1) return macDinh;
  return Math.floor(so);
}

export function usePhanTrangUrl(perPageMacDinh: number = PER_PAGE_MAC_DINH) {
  const [params, setParams] = useSearchParams();

  const page = docSo(params.get('page'), 1);
  const perPageDocDuoc = docSo(params.get('perPage'), perPageMacDinh);
  /*
   * Chốt danh sách cho phép. Thiếu bước này thì `?perPage=100000` gõ tay trên
   * thanh địa chỉ thành một truy vấn nặng mà không ai cố ý gửi.
   */
  const perPage = (PER_PAGE_OPTIONS as readonly number[]).includes(perPageDocDuoc)
    ? perPageDocDuoc
    : perPageMacDinh;

  /*
   * Gộp mọi thay đổi vào MỘT lời gọi.
   *
   * `setParams(fn)` truyền vào `fn` bản `searchParams` bắt được lúc render, chứ
   * không phải bản mới nhất. Gọi hai lần liên tiếp trong cùng một handler thì
   * lần sau ghi đè lần trước — nên "đổi số dòng rồi về trang 1" buộc phải là
   * một lời gọi, không phải hai.
   */
  const vaThamSo = useCallback(
    (thayDoi: ThayDoiThamSo, thayThe = false) => {
      setParams(
        (truoc) => {
          const sau = new URLSearchParams(truoc);
          for (const [khoa, giaTri] of Object.entries(thayDoi)) {
            if (giaTri === null) sau.delete(khoa);
            else sau.set(khoa, giaTri);
          }
          return sau;
        },
        thayThe ? { replace: true } : undefined,
      );
    },
    [setParams],
  );

  /* Trang 1 và số dòng mặc định không cần nằm trên URL — giữ thanh địa chỉ sạch. */
  const datTrang = useCallback(
    (trang: number) => vaThamSo({ page: trang <= 1 ? null : String(trang) }),
    [vaThamSo],
  );

  const datSoDong = useCallback(
    (soDong: number) =>
      vaThamSo({
        perPage: soDong === perPageMacDinh ? null : String(soDong),
        page: null,
      }),
    [vaThamSo, perPageMacDinh],
  );

  /** Đổi bộ lọc thì luôn về trang đầu — trang 7 của kết quả cũ không có nghĩa. */
  const datLoc = useCallback(
    (thayDoi: ThayDoiThamSo) => vaThamSo({ ...thayDoi, page: null }),
    [vaThamSo],
  );

  return { params, page, perPage, vaThamSo, datTrang, datSoDong, datLoc };
}
